/**
 * MessageThread — the shared conversation surface (message list + composer)
 * behind BOTH the public game chat and the private direct-message screens.
 * Source of truth for the cross-platform keyboard handling; callers supply the
 * data + header via props.
 *
 * ⚠️ Keep this outside RN Modal/AppModal overlays. The game chat drawer is a
 * screen-level drawer so composer keyboard handling remains attached to the
 * native/web viewport.
 * The `domIdPrefix` prop namespaces the web-only DOM ids so two thread surfaces
 * never collide (chat = 'chat', direct messages = 'dm').
 *
 * ── Keyboard handling: sit the composer flush on the soft keyboard ──────────
 * No dead gap between the input bar and the keyboard on any target. Each
 * platform needs a different mechanism (verified against real devices; PRs
 * #553–#557):
 *
 *   NATIVE (iOS + Android)
 *     • <KeyboardAvoidingView> shifts the messages+composer column above the
 *       keyboard ('padding' on iOS, 'height' on Android).
 *     • The composer drops its home-indicator / gesture-nav safe-area inset
 *       WHILE the keyboard is up (`keyboardUp`) — the keyboard already covers
 *       that region, so keeping the inset showed as a gap.
 *
 *   WEB (mobile browsers) — cooperating pieces:
 *     1. `#<prefix>-outer` is pinned to `window.visualViewport` (position:fixed
 *        at offsetTop, sized to vv width/height). Mobile Safari does NOT shrink
 *        the layout viewport when the keyboard opens, so pinning keeps the flex
 *        children filling exactly the area above the keyboard.
 *     2. `interactive-widget=resizes-content` in the web viewport meta
 *        (public/index.html) makes ANDROID shrink the layout viewport.
 *     3. `window.scrollTo(0,0)` on every visual-viewport update cancels iOS
 *        WebKit's focus auto-scroll (which would shift the fixed layer).
 *     4. A plain flex View on web (NOT KeyboardAvoidingView) — RN Web's KAV
 *        adds a stray bottom inset on top of the pinning, which is itself a gap.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  View,
  Text,
  ScrollView,
  TextInput,
  TouchableOpacity,
  KeyboardAvoidingView,
  Keyboard,
  Platform,
  Pressable,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import type { ChatReactionEmoji } from '@warcarp/api-types'
import { ChatComposerInput } from './ChatComposerInput'
import { reactionFlowLayout } from '../../lib/reactionFlow'
import { ChatMessageText } from './ChatMessageText'
import { isCoarsePointerWeb } from '../../lib/platform'
import { reportError } from '../../lib/reporting'
import { resolveDividerBoundaryId } from '../../lib/threadDivider'
import { pickThreadScrollTarget } from '../../lib/threadScrollTarget'
import { NewMessageDivider } from './NewMessageDivider'
import { EmojiPickerModal, MessageActionTray, ReactionChips, useMessageActions } from './MessageActions'
import { GAME_TOP_BAR_HEIGHT } from './GameTopBar'
import { contrastTextColor } from '../map/badgeHelpers'

/** Normalised message shape so the component is source-agnostic (chat / DM). */
export interface ThreadReaction {
  emoji: ChatReactionEmoji
  userId: string
  displayName: string
}

export interface ThreadMessage {
  id: string
  authorId: string
  authorName: string
  content: string
  createdAt: string
  /** Emoji reactions shared by public chat and private messages. */
  reactions?: ThreadReaction[]
  /** The author's player colour (group threads with several senders): tints
   *  their avatar. The name label always shows too, so colour is never the
   *  only cue. */
  authorColor?: string
}

function formatTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' })
}

export function MessageBubble({
  item,
  isOwn,
  showMeta,
  showAvatar = true,
  topSpacing,
  onReact,
  currentUserId,
  isReacting,
  isFocused,
  suppressTextSelection,
  onReport,
  readOnly = false,
  palette,
  onOpenLink,
}: {
  item: ThreadMessage
  isOwn: boolean
  showMeta: boolean
  /** Evidence previews keep sender metadata but omit the avatar and its gutter. */
  showAvatar?: boolean
  /** Override grouping space for embedded message previews. */
  topSpacing?: number
  /** Reaction toggle for the shared compact emoji palette. */
  onReact?: (messageId: string, emoji: ChatReactionEmoji) => void
  currentUserId?: string
  isReacting?: boolean
  /** Deep-link target (`?chat=<id>`): scrolled into view and outlined. */
  isFocused?: boolean
  /** Touch browsers reserve long-press for message actions, not native selection. */
  suppressTextSelection: boolean
  onReport?: (message: ThreadMessage) => void
  readOnly?: boolean
  /** Return true when the host handled navigation; otherwise open the URL normally. */
  onOpenLink?: (href: string) => boolean
  /** Explicit colors for native consumers without the game's NativeWind theme. */
  palette?: { background: string; text: string; muted: string; border?: string }
}) {
  const [rowWidth, setRowWidth] = useState(320)
  const [hovered, setHovered] = useState(false)
  const [chipHover, setChipHover] = useState(false)
  const reactions = item.reactions ?? []
  const reactionLayout = reactionFlowLayout(rowWidth, Array.from(new Set(reactions.map(reaction => reaction.emoji))).map(emoji => reactions.filter(reaction => reaction.emoji === emoji).length))
  const actions = useMessageActions({ messageId: item.id, content: item.content, alignEnd: isOwn, onReact })
  const showActionTray = !readOnly && (hovered || actions.trayOpen)

  return (
    <>
      <View
      onLayout={event => setRowWidth(event.nativeEvent.layout.width)}
      className="flex-row items-end"
      style={{
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 8,
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        marginTop: topSpacing ?? (showMeta ? 16 : 3),
        position: 'relative',
        // A child z-index cannot reliably outrank later ScrollView siblings on
        // native/web. Lift the whole message while any floating surface is open.
        zIndex: showActionTray || chipHover ? 100 : 0,
      }}
    >
      {!isOwn && showAvatar &&
        (showMeta ? (
          <View
            style={{
              width: 28,
              height: 28,
              borderRadius: 14,
              backgroundColor: item.authorColor ?? '#334155',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Text style={{ fontSize: 12, fontWeight: '700', color: item.authorColor ? contrastTextColor(item.authorColor) : '#fff', lineHeight: 28 }}>
              {item.authorName?.charAt(0).toUpperCase() ?? '?'}
            </Text>
          </View>
        ) : (
          <View style={{ width: 28 }} />
        ))}

      <View style={{ maxWidth: '74%', alignItems: isOwn ? 'flex-end' : 'flex-start' }}>
        {showMeta && !isOwn && (
          <Text className="text-ink-soft font-typed" style={{ fontSize: 11, marginBottom: 3, marginLeft: 2, ...(palette ? { color: palette.muted } : {}) }}>
            {item.authorName}
          </Text>
        )}
        <View
          onPointerEnter={() => {
            setHovered(true)
          }}
          onPointerLeave={() => {
            setHovered(false)
            if (!actions.trayOpen) actions.setPickerOpen(false)
          }}
          style={{ position: 'relative', alignSelf: isOwn ? 'flex-end' : 'flex-start' }}
        >
          <Pressable
            disabled={readOnly}
            onPress={actions.handlePress}
            onLongPress={actions.handleLongPress}
            onPressOut={actions.handlePressOut}
            delayLongPress={350}
            accessibilityLabel={`Message from ${item.authorName}: ${item.content}`}
            accessibilityHint={onReact ? 'Long press for reactions and copy' : 'Long press to copy'}
            className={isOwn ? 'bg-blood' : 'bg-paper-up'}
            style={{
              ...(palette ? { backgroundColor: palette.background } : {}),
              ...(palette?.border ? { borderWidth: 1, borderColor: palette.border } : {}),
              paddingHorizontal: 14,
              paddingVertical: 9,
              borderRadius: 18,
              borderBottomRightRadius: isOwn ? 4 : 18,
              borderBottomLeftRadius: isOwn ? 18 : 4,
              // Deep-link orientation: outline the linked-to message so the
              // reader lands on the right bubble (outline + scroll, never
              // colour alone). Own bubbles are blood-red, where a blood outline
              // is invisible — those get a cream outline instead.
              ...(isFocused
                ? { borderWidth: 2, borderColor: isOwn ? '#e6e1c4' : '#a3341f' }
                : null),
              ...(suppressTextSelection
                ? { userSelect: 'none' }
                : null),
            }}
          >
            <Text
              selectable={Platform.OS === 'web' && !suppressTextSelection}
              className={`font-typed ${isOwn ? 'text-white' : 'text-ink'}`}
              style={{ fontSize: 15, lineHeight: 21, ...(palette ? { color: palette.text } : {}) }}
            >
              {<ChatMessageText content={item.content} onOpenLink={onOpenLink} />}
            </Text>
          </Pressable>

          {showActionTray && (
            <View
              style={{
                position: 'absolute',
                bottom: '100%',
                ...(isOwn ? { right: 0 } : { left: 0 }),
                alignItems: isOwn ? 'flex-end' : 'flex-start',
                // Tuck the tray into the bubble's top padding without covering
                // the first line of message text (the bubble has 9px top padding).
                transform: [{ translateY: 8 }],
                zIndex: 20,
              }}
            >
              <MessageActionTray
                trayRef={actions.trayRef}
                reactions={reactions}
                currentUserId={currentUserId}
                isReacting={isReacting}
                canReact={!!onReact}
                copied={actions.copied}
                onToggleReaction={actions.toggleReaction}
                onOpenPicker={actions.openPicker}
                onCopy={() => { void actions.copy() }}
                onReport={!isOwn && onReport ? () => { actions.close(); onReport(item) } : undefined}
              />
            </View>
          )}
        </View>

        <ReactionChips
          size="small"
          maxPerRow={reactionLayout.columns || 3}
          reactions={reactions}
          currentUserId={currentUserId}
          isReacting={isReacting}
          canReact={!!onReact}
          alignEnd={isOwn}
          onToggleReaction={actions.toggleReaction}
          onHoverChange={setChipHover}
          style={{ marginTop: 4, width: reactionLayout.width || undefined, paddingHorizontal: 0, paddingVertical: 4, zIndex: 4 }}
        />

        <View className="flex-row items-center" style={{ marginTop: 3, marginHorizontal: 2 }}>
          <Text className="text-ink-soft font-typed" style={{ fontSize: 10, opacity: 0.7, ...(palette ? { color: palette.muted } : {}) }}>
            {formatTime(item.createdAt)}
          </Text>
        </View>
      </View>
      </View>
      {onReact && actions.pickerOpen && (
        <EmojiPickerModal
          position={actions.pickerPosition}
          reactions={reactions}
          currentUserId={currentUserId}
          isReacting={isReacting}
          onToggleReaction={actions.toggleReaction}
          onClose={actions.close}
        />
      )}
    </>
  )
}

export interface MessageThreadProps {
  onReport?: (message: ThreadMessage) => void
  title: string
  subtitle?: string
  messages: ThreadMessage[]
  currentUserId: string | undefined
  onSend: (content: string) => Promise<void>
  isSending: boolean
  composerDisabled?: boolean
  onClose: () => void
  emptyIcon?: string
  emptyText?: string
  placeholder?: string
  /** Index of the first unread message — renders a "NEW" divider above it.
   *  -1 (default) shows no divider. */
  firstUnreadIndex?: number
  /** Called to mark the thread seen/read — fired when the composer gains focus
   *  and again when the thread is left (unmounted). The "NEW" divider stays
   *  visible until the first of those happens. */
  onSeen?: () => void
  /** Namespaces the web-only DOM ids (e.g. 'dm'), so surfaces never collide. */
  domIdPrefix: string
  /** Optional reaction toggle supplied by public chat and direct messages. */
  onReact?: (messageId: string, emoji: ChatReactionEmoji) => void
  /** Message whose reaction mutation is currently in flight. */
  reactingMessageId?: string
  /** Deep-link target (a `?chat=<id>` link opens the drawer here): scroll into view and
   *  outline the selected bubble for orientation. */
  focusMessageId?: string
  /** Drawer presentation drops route safe-area padding and uses drawer keyboard geometry. */
  presentation?: 'screen' | 'drawer'
  /** True while a retained drawer is open. Screen routes are always active. */
  isActive?: boolean
  /** Distance from the screen top to the drawer's top edge. */
  keyboardVerticalOffset?: number
}

export function MessageThread({
  onReport,
  title,
  subtitle,
  messages,
  currentUserId,
  onSend,
  isSending,
  composerDisabled = false,
  onClose,
  emptyIcon = '🔒',
  emptyText = 'No messages yet.',
  placeholder = 'Message…',
  firstUnreadIndex = -1,
  onSeen,
  domIdPrefix,
  onReact,
  reactingMessageId,
  focusMessageId,
  presentation = 'screen',
  isActive = true,
  keyboardVerticalOffset = 0,
}: MessageThreadProps) {
  const insets = useSafeAreaInsets()
  const isWeb = Platform.OS === 'web'
  const suppressMessageTextSelection = isCoarsePointerWeb()
  // Instance-UNIQUE DOM ids. Two thread surfaces can coexist in the
  // navigation stack (for example a drawer and a direct-message route): with a shared id,
  // the new screen's web pinning effect styled the FIRST (hidden) element via
  // getElementById, so the visible surface never got pinned — it sat in
  // normal flow, content-sized, "scrolled up" with a void underneath.
  const uidRef = useRef(Math.random().toString(36).slice(2, 8))
  const outerId = `${domIdPrefix}-outer-${uidRef.current}`
  const messagesId = `${domIdPrefix}-messages-${uidRef.current}`

  // "NEW" divider boundary.
  //
  // Watermark = the newest message id present when we ARRIVED (first render with
  // data). The divider may only sit on a message at/before that watermark — a
  // message that arrives afterwards was seen live, so it never spawns a divider
  // (that was the "flash while live-chatting" bug).
  //
  // We keep re-evaluating (not "settle once on first data") because a boundary
  // input can resolve async — chat's last-seen id loads from storage AFTER the
  // messages do. Once a boundary is accepted we freeze it by id, so later churn
  // (the DM markRead refetch flipping readAt) can't recompute it away and
  // unmount the divider before its fade. Remounting resolves fresh.
  const watermarkIdRef = useRef<string | undefined>(undefined)
  if (watermarkIdRef.current === undefined && messages.length > 0) {
    watermarkIdRef.current = messages[messages.length - 1]?.id
  }
  const boundaryIdRef = useRef<string | null>(null)
  boundaryIdRef.current = resolveDividerBoundaryId(
    messages,
    firstUnreadIndex,
    currentUserId,
    watermarkIdRef.current,
    boundaryIdRef.current,
  )
  const dividerIndex = boundaryIdRef.current
    ? messages.findIndex((m) => m.id === boundaryIdRef.current)
    : -1

  const [text, setText] = useState('')
  const [sendError, setSendError] = useState<string | null>(null)
  const [keyboardUp, setKeyboardUp] = useState(false)
  // The "NEW" divider stays visible until the composer is focused or the thread
  // is left — see handleComposerFocus + the unmount effect below.
  const [dividerDismissed, setDividerDismissed] = useState(false)
  const scrollRef = useRef<ScrollView>(null)
  const inputRef = useRef<TextInput>(null)
  const messageOffsetsRef = useRef(new Map<string, number>())
  const focusedMessageScrolledRef = useRef<string | null>(null)
  const focusedMessageLengthRef = useRef<number | null>(null)
  const lastFocusMessageIdRef = useRef(focusMessageId)
  if (lastFocusMessageIdRef.current !== focusMessageId) {
    lastFocusMessageIdRef.current = focusMessageId
    focusedMessageScrolledRef.current = null
    focusedMessageLengthRef.current = null
  }

  // Mark the thread seen/read when the viewer leaves it (unmount), if they
  // didn't already by focusing the composer. Deliberately NO autofocus: opening
  // the composer would instantly count as "focused" and hide the NEW divider
  // before it could be read.
  const onSeenRef = useRef(onSeen)
  onSeenRef.current = onSeen
  useEffect(() => {
    return () => {
      onSeenRef.current?.()
    }
  }, [])

  const handleComposerFocus = useCallback(() => {
    setDividerDismissed(true)
    onSeenRef.current?.()
  }, [])

  // Closing the thread also marks it seen and dismisses the divider — the
  // screen may be CACHED (not unmounted) between close→reopen, so we can't rely
  // on the unmount effect alone; doing it here means a cached reopen shows no
  // stale divider. onClose then navigates away.
  const handleClose = useCallback(() => {
    setDividerDismissed(true)
    onSeenRef.current?.()
    onClose()
  }, [onClose])

  const scrollToEnd = useCallback((animated = false) => {
    scrollRef.current?.scrollToEnd({ animated })
  }, [])

  const scrollToContentPosition = useCallback(() => {
    const target = pickThreadScrollTarget({
      focusId: focusMessageId,
      hasFocusMessage: messages.some((message) => message.id === focusMessageId),
      focusedMessageId: focusedMessageScrolledRef.current,
      focusedMessageLength: focusedMessageLengthRef.current,
      messageLength: messages.length,
    })
    if (target === 'end') {
      scrollToEnd(false)
      return
    }
    if (target === 'preserve') return

    // Wait for the focused row's layout, then remember the current list length
    // so future arrivals return to the normal tail-following behavior.
    const y = focusMessageId ? messageOffsetsRef.current.get(focusMessageId) : undefined
    if (y === undefined || !focusMessageId) return
    scrollRef.current?.scrollTo({ y: Math.max(0, y - 12), animated: false })
    focusedMessageScrolledRef.current = focusMessageId
    focusedMessageLengthRef.current = messages.length
  }, [focusMessageId, messages, scrollToEnd])

  // Desktop web: Esc closes (matches chat / roster panel).
  useEffect(() => {
    if (Platform.OS !== 'web' || !isActive || presentation === 'drawer') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [handleClose, isActive, presentation])

  useEffect(() => {
    if (messages.length > 0) scrollToContentPosition()
  }, [messages.length, scrollToContentPosition])

  // Native: track keyboard to collapse the composer's safe-area padding while up.
  useEffect(() => {
    if (Platform.OS === 'web' || !isActive) return
    const showEvt = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow'
    const hideEvt = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide'
    const onShow = Keyboard.addListener(showEvt, () => {
      setKeyboardUp(true)
      scrollToEnd(false)
    })
    const onHide = Keyboard.addListener(hideEvt, () => setKeyboardUp(false))
    return () => {
      onShow.remove()
      onHide.remove()
    }
  }, [isActive, scrollToEnd])

  // Web: pin the surface to the visual viewport so the composer rests on the
  // keyboard (see chat.tsx for the full rationale). domIdPrefix namespaces ids.
  useEffect(() => {
    if (!isWeb || !isActive) return
    const style = document.createElement('style')
    style.dataset.threadOverlay = domIdPrefix
    style.textContent =
      'html,body{overflow:hidden!important;height:100%!important;' +
      'overscroll-behavior:none!important;touch-action:pan-x pan-y;}' +
      `#${outerId}{overflow:hidden!important;}`
    document.head.appendChild(style)

    const el = document.getElementById(outerId)

    let caretKicked = false
    const kickCaret = () => {
      if (caretKicked) return
      const ta = el?.querySelector('textarea, input') as
        | HTMLTextAreaElement
        | HTMLInputElement
        | null
      if (!ta || document.activeElement !== ta) return
      caretKicked = true
      requestAnimationFrame(() => {
        ta.focus()
        const len = ta.value.length
        try {
          ta.setSelectionRange(len, len)
        } catch {
          /* some input types disallow setSelectionRange */
        }
      })
    }

    const applyViewport = () => {
      if (window.pageYOffset !== 0 || window.scrollY !== 0) window.scrollTo(0, 0)
      const vv = window.visualViewport
      if (el && presentation === 'screen') {
        const w = vv ? vv.width : window.innerWidth
        const h = vv ? vv.height : window.innerHeight
        const top = vv ? vv.offsetTop : 0
        const left = vv ? vv.offsetLeft : 0
        el.style.position = 'fixed'
        el.style.top = `${top}px`
        el.style.left = `${left}px`
        el.style.width = `${w}px`
        el.style.height = `${h}px`
      }
      scrollToEnd(false)
      kickCaret()
    }
    applyViewport()

    const blockBodyScroll = (e: TouchEvent) => {
      const list = document.getElementById(messagesId)
      let node: Node | null = e.target as Node | null
      while (node) {
        if (node === list) return
        node = node.parentNode
      }
      e.preventDefault()
    }
    document.addEventListener('touchmove', blockBodyScroll, { passive: false })

    const vv = window.visualViewport
    vv?.addEventListener('resize', applyViewport)
    vv?.addEventListener('scroll', applyViewport)
    window.addEventListener('resize', applyViewport)

    return () => {
      try {
        document.head.removeChild(style)
      } catch {
        /* already removed */
      }
      document.removeEventListener('touchmove', blockBodyScroll)
      vv?.removeEventListener('resize', applyViewport)
      vv?.removeEventListener('scroll', applyViewport)
      window.removeEventListener('resize', applyViewport)
    }
  }, [isWeb, isActive, presentation, scrollToEnd, outerId, messagesId, domIdPrefix])

  async function handleSend() {
    const trimmed = text.trim()
    if (!trimmed || isSending || composerDisabled) return
    setSendError(null)
    setText('')
    try {
      await onSend(trimmed)
      scrollToEnd(true)
    } catch {
      setSendError("Couldn't send — please try again.")
      setText(trimmed)
    }
  }

  const Body = isWeb ? View : KeyboardAvoidingView
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const bodyProps: any = isWeb
    ? { style: { flex: 1 } }
    : {
        style: { flex: 1 },
        behavior: Platform.OS === 'ios' ? 'padding' : 'height',
        keyboardVerticalOffset: presentation === 'drawer' ? keyboardVerticalOffset : 0,
      }

  return (
    <View
      nativeID={outerId}
      className="bg-canvas"
      style={
        isWeb
          ? { flex: 1 }
          : {
              flex: 1,
              paddingTop: presentation === 'screen' ? insets.top : 0,
              paddingLeft: insets.left,
              paddingRight: insets.right,
            }
      }
    >
      {/* Header */}
      <View
        className="flex-row items-center bg-canvas border-b border-olive"
        style={{ paddingHorizontal: 16, height: GAME_TOP_BAR_HEIGHT }}
      >
        {presentation === 'drawer' && (
          <TouchableOpacity
            onPress={handleClose}
            accessibilityLabel="Close chat"
            accessibilityRole="button"
            className="mr-2"
            style={{ minHeight: 40, minWidth: 40, alignItems: 'center', justifyContent: 'center' }}
          >
            <Text className="text-ink text-xl">×</Text>
          </TouchableOpacity>
        )}
        <View className="flex-1">
          <Text className="text-ink font-stencil text-lg uppercase" numberOfLines={1}>
            {title}
          </Text>
          {subtitle ? (
            <Text className="text-ink-soft font-typed text-xs" numberOfLines={1}>
              {subtitle}
            </Text>
          ) : null}
        </View>
        {presentation === 'screen' && (
          <TouchableOpacity
            onPress={handleClose}
            accessibilityLabel="Close"
            accessibilityRole="button"
            className="px-2"
            style={{ minHeight: 32, minWidth: 32, alignItems: 'center', justifyContent: 'center' }}
          >
            <Text className="text-ink text-xl">×</Text>
          </TouchableOpacity>
        )}
      </View>

      <Body {...bodyProps}>
        <ScrollView
          ref={scrollRef}
          nativeID={messagesId}
          className="flex-1 bg-paper"
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: 'flex-end',
            paddingHorizontal: 12,
            paddingTop: 12,
            paddingBottom: 8,
          }}
          keyboardShouldPersistTaps="handled"
          onContentSizeChange={scrollToContentPosition}
          onLayout={scrollToContentPosition}
        >
          {messages.length === 0 ? (
            <View className="items-center justify-center" style={{ flex: 1, paddingVertical: 48 }}>
              <Text style={{ fontSize: 36, marginBottom: 10 }}>{emptyIcon}</Text>
              <Text className="text-ink-soft font-typed" style={{ fontSize: 14 }}>
                {emptyText}
              </Text>
            </View>
          ) : (
            messages.map((item, idx) => {
              const isOwn = item.authorId === currentUserId
              const showMeta = messages[idx - 1]?.authorId !== item.authorId
              return (
                <View
                  key={item.id}
                  collapsable={false}
                  onLayout={(event) => {
                    messageOffsetsRef.current.set(item.id, event.nativeEvent.layout.y)
                    if (item.id === focusMessageId) scrollToContentPosition()
                  }}
                >
                  {idx === dividerIndex && <NewMessageDivider dismissed={dividerDismissed} />}
                  <MessageBubble
                    item={item}
                    isOwn={isOwn}
                    showMeta={showMeta}
                    onReact={onReact}
                    onReport={onReport}
                    currentUserId={currentUserId}
                    isReacting={reactingMessageId === item.id}
                    isFocused={item.id === focusMessageId}
                    suppressTextSelection={suppressMessageTextSelection}
                  />
                </View>
              )
            })
          )}
        </ScrollView>

        {sendError && (
          <Text
            className="text-blood font-typed text-center bg-canvas"
            style={{ fontSize: 12, paddingHorizontal: 16, paddingTop: 4 }}
          >
            {sendError}
          </Text>
        )}

        <View
          className="flex-row items-end bg-canvas border-t border-olive"
          style={{
            gap: 8,
            paddingHorizontal: 12,
            paddingTop: 10,
            paddingBottom: isWeb || keyboardUp ? 10 : Math.max(insets.bottom, 10),
          }}
        >
          <ChatComposerInput
            variant="bubble"
            inputRef={inputRef}
            testID="message-thread-composer"
            value={text}
            disabled={composerDisabled}
            onFocus={handleComposerFocus}
            onChangeText={(t) => {
              setText(t)
              if (sendError) setSendError(null)
            }}
            onSend={() => { void handleSend() }}
            placeholder={placeholder}
            isSending={isSending}
          />
        </View>
      </Body>
    </View>
  )
}
